import React, { useState, useMemo } from "react";
import {
  Users,
  Search,
  Filter,
  Mail,
  MoreVertical,
  ExternalLink,
  CheckCircle2,
  Clock,
  ChevronRight,
  BookOpen,
  X,
  Send,
  ArrowUp,
  ArrowDown,
  GitBranch,
  Phone,
  Layers,
  Award,
  RefreshCw,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import InputField from "../../components/common/InputField";
import { useToast } from "../../hooks/otherHooks/useToast";
import {
  useInstructorStudentsQuery,
  useSendInstructorMessageMutation,
} from "../../hooks/queries/useCurriculumAdminQueries";
import { InstructorStudentItem } from "../../services/curriculumService";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/147/147142.png";

// --- Sub-components ---

const GlassCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}
  >
    {children}
  </div>
);

const ProgressBar = ({ progress }: { progress: number }) => {
  const safeProgress = Math.min(Math.max(Number(progress) || 0, 0), 100);
  return (
    <div className="cht-w-full cht-max-w-[120px] cht-space-y-1.5">
      <div className="cht-flex cht-justify-between cht-items-center cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest">
        <span
          className={
            safeProgress === 100 ? "cht-text-green-500" : "cht-text-gray-400"
          }
        >
          {safeProgress}%
        </span>
      </div>
      <div className="cht-w-full cht-h-1.5 cht-bg-gray-100 cht-rounded-full cht-overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${safeProgress}%` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={`cht-h-full cht-rounded-full ${
            safeProgress === 100 ? "cht-bg-green-500" : "cht-bg-[#3e57da]"
          }`}
        />
      </div>
    </div>
  );
};

interface MessageModalProps {
  onClose: () => void;
  recipientStudent?: InstructorStudentItem | null;
  courseOptions: string[];
}

const MessageModal: React.FC<MessageModalProps> = ({
  onClose,
  recipientStudent = null,
  courseOptions,
}) => {
  const { addToast } = useToast();
  const sendMessageMutation = useSendInstructorMessageMutation();

  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const isBroadcast = !recipientStudent;

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      addToast("Please enter both a subject and message content.", "warning");
      return;
    }

    try {
      const res = await sendMessageMutation.mutateAsync({
        subject: subject.trim(),
        message: message.trim(),
        recipient_student_id: recipientStudent?.student_id ?? undefined,
        recipient_email: recipientStudent?.email ?? undefined,
        audience: isBroadcast ? audience : "direct",
      });
      addToast(res.message || "Message sent successfully!", "success");
      onClose();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Failed to send message to student(s).";
      addToast(detail, "error");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="cht-fixed cht-inset-0 cht-z-[150] cht-flex cht-items-center cht-justify-center cht-p-4 md:cht-p-6"
    >
      <div
        className="cht-fixed cht-inset-0 cht-bg-slate-900/60 cht-backdrop-blur-md"
        onClick={onClose}
      />
      <GlassCard className="cht-relative cht-w-full cht-max-w-xl cht-rounded-[40px] cht-p-10 cht-space-y-8 cht-bg-white">
        <div className="cht-flex cht-items-center cht-justify-between">
          <div className="cht-space-y-1">
            <h3 className="cht-text-2xl cht-font-black cht-text-black cht-tracking-tight">
              {isBroadcast ? "Broadcast Message" : "Direct Message"}
            </h3>
            <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
              {isBroadcast
                ? "Send in-app notification to enrolled students"
                : `Recipient: ${recipientStudent?.name} (${recipientStudent?.email})`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 cht-rounded-full hover:cht-bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="cht-space-y-6">
          {isBroadcast && (
            <div className="cht-space-y-2">
              <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">
                Target Audience
              </label>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-p-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
              >
                <option value="all">All Enrolled Students</option>
                <option value="low_progress">
                  Students with Progress &lt; 50%
                </option>
                {courseOptions
                  .filter((c) => c !== "All Courses")
                  .map((courseTitle) => (
                    <option key={courseTitle} value={courseTitle}>
                      {courseTitle} Only
                    </option>
                  ))}
              </select>
            </div>
          )}

          <InputField
            label="Subject"
            placeholder="e.g. Update regarding your course"
            value={subject}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSubject(e.target.value)
            }
          />

          <div className="cht-space-y-2">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">
              Message Content
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-p-6 cht-text-sm cht-font-medium cht-min-h-[150px] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
              placeholder="Type your message here..."
            />
          </div>
        </div>

        <div className="cht-flex cht-gap-4">
          <Button variant="ghost" onClick={onClose} className="cht-flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            className="cht-flex-1 cht-rounded-xl"
            leftIcon={<Send size={18} />}
            onClick={handleSend}
            disabled={sendMessageMutation.isPending}
          >
            {sendMessageMutation.isPending ? "Sending..." : "Send Message"}
          </Button>
        </div>
      </GlassCard>
    </motion.div>
  );
};

interface FilterPopoverProps {
  onClose: () => void;
  sortConfig: { key: string; direction: string };
  setSortConfig: React.Dispatch<
    React.SetStateAction<{ key: string; direction: string }>
  >;
  statusFilters: { completed: boolean; inProgress: boolean };
  setStatusFilters: React.Dispatch<
    React.SetStateAction<{ completed: boolean; inProgress: boolean }>
  >;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  onClose,
  sortConfig,
  setSortConfig,
  statusFilters,
  setStatusFilters,
}) => {
  const toggleStatus = (key: "completed" | "inProgress") => {
    setStatusFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="cht-absolute cht-right-0 cht-top-full cht-mt-4 cht-w-72 cht-z-[50]"
    >
      <GlassCard className="cht-p-8 cht-rounded-[32px] cht-bg-white cht-shadow-2xl cht-space-y-6">
        <div className="cht-space-y-4">
          <h4 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
            Sort By
          </h4>
          <div className="cht-grid cht-grid-cols-1 cht-gap-1">
            {[
              { label: "Name", key: "name" },
              { label: "Progress", key: "progress" },
              { label: "Activity", key: "lastActiveTimestamp" },
            ].map((sort) => (
              <button
                key={sort.key}
                onClick={() =>
                  setSortConfig({
                    key: sort.key,
                    direction:
                      sortConfig.key === sort.key &&
                      sortConfig.direction === "asc"
                        ? "desc"
                        : "asc",
                  })
                }
                className={`cht-flex cht-items-center cht-justify-between cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-xs cht-font-bold cht-transition-all ${
                  sortConfig.key === sort.key
                    ? "cht-bg-[#3e57da]/10 cht-text-[#3e57da]"
                    : "cht-text-gray-500 hover:cht-bg-gray-50"
                }`}
              >
                {sort.label}
                {sortConfig.key === sort.key &&
                  (sortConfig.direction === "asc" ? (
                    <ArrowUp size={12} />
                  ) : (
                    <ArrowDown size={12} />
                  ))}
              </button>
            ))}
          </div>
        </div>

        <div className="cht-space-y-4">
          <h4 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
            Progress Range
          </h4>
          <div className="cht-space-y-3">
            <button
              onClick={() => toggleStatus("completed")}
              className="cht-flex cht-items-center cht-gap-3 cht-w-full group"
            >
              <div
                className={`cht-w-5 cht-h-5 cht-rounded-lg cht-border-2 cht-flex cht-items-center cht-justify-center cht-transition-all ${
                  statusFilters.completed
                    ? "cht-bg-[#3e57da] cht-border-[#3e57da] cht-text-white"
                    : "cht-border-gray-200"
                }`}
              >
                {statusFilters.completed && <CheckCircle2 size={12} />}
              </div>
              <span
                className={`cht-text-xs cht-font-bold cht-transition-colors ${
                  statusFilters.completed
                    ? "cht-text-[#3e57da]"
                    : "cht-text-gray-500 group-hover:cht-text-gray-700"
                }`}
              >
                Completed (100%)
              </span>
            </button>
            <button
              onClick={() => toggleStatus("inProgress")}
              className="cht-flex cht-items-center cht-gap-3 cht-w-full group"
            >
              <div
                className={`cht-w-5 cht-h-5 cht-rounded-lg cht-border-2 cht-flex cht-items-center cht-justify-center cht-transition-all ${
                  statusFilters.inProgress
                    ? "cht-bg-[#3e57da] cht-border-[#3e57da] cht-text-white"
                    : "cht-border-gray-200"
                }`}
              >
                {statusFilters.inProgress && <CheckCircle2 size={12} />}
              </div>
              <span
                className={`cht-text-xs cht-font-bold cht-transition-colors ${
                  statusFilters.inProgress
                    ? "cht-text-[#3e57da]"
                    : "cht-text-gray-500 group-hover:cht-text-gray-700"
                }`}
              >
                In Progress (&lt; 100%)
              </span>
            </button>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          className="cht-w-full cht-rounded-xl"
          onClick={onClose}
        >
          Apply & Close
        </Button>
      </GlassCard>
    </motion.div>
  );
};

interface StudentDetailViewProps {
  student: InstructorStudentItem;
  onClose: () => void;
  onMessage: (student: InstructorStudentItem) => void;
}

const StudentDetailView: React.FC<StudentDetailViewProps> = ({
  student,
  onClose,
  onMessage,
}) => (
  <motion.div
    initial={{ x: "100%" }}
    animate={{ x: 0 }}
    exit={{ x: "100%" }}
    transition={{ type: "spring", stiffness: 300, damping: 30 }}
    className="cht-fixed cht-right-0 cht-top-0 cht-h-screen cht-w-full md:cht-w-[500px] cht-bg-white cht-z-[10001] cht-shadow-2xl cht-flex cht-flex-col"
  >
    <div className="cht-p-10 cht-border-b cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
      <h3 className="cht-text-xl cht-font-black">Student Details</h3>
      <button
        onClick={onClose}
        className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 cht-rounded-full hover:cht-bg-gray-100 transition-colors"
      >
        <ChevronRight size={20} />
      </button>
    </div>

    <div className="cht-flex-1 cht-overflow-y-auto cht-p-10 cht-space-y-10">
      <div className="cht-flex cht-items-center cht-gap-6">
        <div className="cht-w-24 cht-h-24 cht-rounded-[32px] cht-overflow-hidden cht-shadow-xl cht-bg-gray-50 cht-flex-shrink-0">
          <img
            src={student.avatar || DEFAULT_AVATAR}
            alt={student.name}
            className="cht-w-full cht-h-full cht-object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
            }}
          />
        </div>
        <div className="cht-space-y-1.5 cht-min-w-0">
          <h4 className="cht-text-2xl cht-font-black cht-text-black cht-truncate">
            {student.name}
          </h4>
          <p className="cht-text-gray-400 cht-font-medium cht-text-sm cht-truncate">
            {student.email}
          </p>
          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-pt-1">
            {student.phone_number && (
              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-bg-gray-50 cht-rounded-lg cht-text-[11px] cht-font-bold cht-text-gray-600">
                <Phone size={11} className="cht-text-gray-400" />
                {student.phone_number}
              </span>
            )}
            {student.github_username && (
              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-bg-slate-900 cht-text-white cht-rounded-lg cht-text-[11px] cht-font-bold">
                <GitBranch size={11} />@{student.github_username}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="cht-space-y-4">
        <div className="cht-flex cht-items-center cht-justify-between">
          <h5 className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-[0.2em] cht-text-[#3e57da]">
            Enrolled Curriculum
          </h5>
          <span
            className={`cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest ${
              student.delivery_mode === "onsite"
                ? "cht-bg-amber-50 cht-text-amber-700"
                : "cht-bg-blue-50 cht-text-[#3e57da]"
            }`}
          >
            {student.delivery_mode} • {student.curriculum_type}
          </span>
        </div>

        <div className="cht-p-6 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-between cht-gap-4">
          <div className="cht-flex cht-items-center cht-gap-4 cht-min-w-0">
            <div className="cht-w-10 cht-h-10 cht-bg-white cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-text-[#3e57da] cht-flex-shrink-0">
              {student.curriculum_type === "track" ? (
                <Layers size={20} />
              ) : (
                <BookOpen size={20} />
              )}
            </div>
            <div className="cht-min-w-0">
              <p className="cht-text-sm cht-font-bold cht-truncate">
                {student.course}
              </p>
              <p className="cht-text-[10px] cht-text-gray-400 cht-font-medium uppercase">
                {student.topics_count ?? 0} Topics • {student.tasks_count ?? 0}{" "}
                Tasks
              </p>
            </div>
          </div>
          <ProgressBar progress={student.progress} />
        </div>
      </div>

      <div className="cht-grid cht-grid-cols-2 cht-gap-4">
        <div className="cht-p-6 cht-bg-blue-50/50 cht-rounded-2xl cht-border cht-border-blue-100">
          <Clock className="cht-text-blue-500 cht-mb-4" size={20} />
          <p className="cht-text-[10px] cht-font-black cht-text-blue-400 cht-uppercase cht-tracking-widest">
            Joined Date
          </p>
          <p className="cht-text-sm cht-font-black cht-text-blue-900">
            {student.joined}
          </p>
        </div>
        <div className="cht-p-6 cht-bg-green-50/50 cht-rounded-2xl cht-border cht-border-green-100">
          <CheckCircle2 className="cht-text-green-500 cht-mb-4" size={20} />
          <p className="cht-text-[10px] cht-font-black cht-text-green-400 cht-uppercase cht-tracking-widest">
            Completed
          </p>
          <p className="cht-text-sm cht-font-black cht-text-green-900">
            {student.completed_lessons_count ?? 0} /{" "}
            {student.total_lessons_count ?? 0} Lessons & Tasks
          </p>
        </div>
      </div>

      {student.submission && (
        <div className="cht-space-y-3">
          <h5 className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-[0.2em] cht-text-[#3e57da]">
            GitHub Autograder Performance
          </h5>
          <div className="cht-p-6 cht-bg-slate-900 cht-text-white cht-rounded-2xl cht-space-y-4">
            <div className="cht-flex cht-items-center cht-justify-between">
              <div className="cht-flex cht-items-center cht-gap-3">
                <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-white/10 cht-flex cht-items-center cht-justify-center">
                  <Award size={18} className="cht-text-amber-400" />
                </div>
                <div>
                  <p className="cht-text-xs cht-font-bold">
                    {student.submission.repository_name}
                  </p>
                  <p className="cht-text-[10px] cht-text-slate-400">
                    {student.submission.passed_tests} /{" "}
                    {student.submission.total_tests} Automated Tests Passed
                  </p>
                </div>
              </div>
              <span
                className={`cht-px-3 cht-py-1 cht-rounded-full cht-text-xs cht-font-black ${
                  student.submission.is_passed
                    ? "cht-bg-emerald-500/20 cht-text-emerald-300"
                    : "cht-bg-amber-500/20 cht-text-amber-300"
                }`}
              >
                {student.submission.score}%
              </span>
            </div>

            {student.submission.repository_url && (
              <a
                href={student.submission.repository_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-bold cht-text-blue-300 hover:cht-text-white cht-no-underline"
              >
                <ExternalLink size={13} />
                Inspect Student Repository on GitHub
              </a>
            )}
          </div>
        </div>
      )}
    </div>

    <div className="cht-p-10 cht-bg-gray-50/50 cht-border-t cht-border-gray-50">
      <Button
        variant="primary"
        className="cht-w-full cht-rounded-xl"
        leftIcon={<Mail size={18} />}
        onClick={() => onMessage(student)}
      >
        Message Student
      </Button>
    </div>
  </motion.div>
);

const InstructorStudents = () => {
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useInstructorStudentsQuery();

  const students = data?.students || [];
  const kpis = data?.kpis;

  const [selectedCourse, setSelectedCourse] = useState("All Courses");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] =
    useState<InstructorStudentItem | null>(null);
  const [openActionRowId, setOpenActionRowId] = useState<string | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] =
    useState<InstructorStudentItem | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Advanced Filter State
  const [sortConfig, setSortConfig] = useState({
    key: "lastActiveTimestamp",
    direction: "desc",
  });
  const [statusFilters, setStatusFilters] = useState({
    completed: false,
    inProgress: false,
  });

  // Build dynamic course filter tabs from DB curricula + enrolled student courses
  const courseFilterOptions = useMemo(() => {
    const titles = new Set<string>(["All Courses"]);
    (data?.courses || []).forEach((c) => {
      if (c.title) titles.add(c.title);
    });
    students.forEach((s) => {
      if (s.course) titles.add(s.course);
    });
    return Array.from(titles);
  }, [data?.courses, students]);

  const filteredStudents = useMemo(() => {
    const result = students.filter((student) => {
      const matchesCourse =
        selectedCourse === "All Courses" || student.course === selectedCourse;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        (student.name || "").toLowerCase().includes(q) ||
        (student.email || "").toLowerCase().includes(q) ||
        (student.course || "").toLowerCase().includes(q) ||
        (student.github_username || "").toLowerCase().includes(q);

      const isCompleted = student.progress === 100;
      const matchesStatus =
        (!statusFilters.completed && !statusFilters.inProgress) ||
        (statusFilters.completed && isCompleted) ||
        (statusFilters.inProgress && !isCompleted);

      return matchesCourse && matchesSearch && matchesStatus;
    });

    // Apply Sorting
    result.sort((a: any, b: any) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(String(bVal))
          : String(bVal).localeCompare(aVal);
      }
      return sortConfig.direction === "asc"
        ? Number(aVal) - Number(bVal)
        : Number(bVal) - Number(aVal);
    });

    return result;
  }, [students, selectedCourse, searchQuery, statusFilters, sortConfig]);

  const handleOpenBroadcast = () => {
    setMessageRecipient(null);
    setShowMessageModal(true);
    setSelectedStudent(null);
    setOpenActionRowId(null);
  };

  const handleOpenDirectMessage = (student: InstructorStudentItem) => {
    setMessageRecipient(student);
    setShowMessageModal(true);
    setSelectedStudent(null);
    setOpenActionRowId(null);
  };

  return (
    <>
      <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
        <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
          <div>
            <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
              Students
            </h1>
            <p className="cht-text-gray-500 cht-font-medium">
              Manage your enrolled students and monitor their live learning
              progress from the database.
            </p>
          </div>
          <div className="cht-flex cht-items-center cht-gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="cht-rounded-2xl cht-bg-white cht-border cht-border-gray-100"
              leftIcon={
                <RefreshCw
                  size={15}
                  className={isFetching ? "cht-animate-spin" : ""}
                />
              }
              onClick={() => refetch()}
            >
              Sync DB
            </Button>
            <Button
              variant="outline"
              leftIcon={<Mail size={18} />}
              className="cht-rounded-2xl"
              onClick={handleOpenBroadcast}
            >
              Broadcast Message
            </Button>
          </div>
        </header>

        {/* Live Database KPI Strip */}
        <div className="cht-grid cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-4">
          {[
            {
              label: "Unique Students",
              value: kpis?.total_students ?? 0,
              sub: `${kpis?.total_enrollments ?? 0} total enrollments`,
            },
            {
              label: "In Progress",
              value: kpis?.in_progress_count ?? 0,
              sub: "Active learners",
            },
            {
              label: "Completed (100%)",
              value: kpis?.completed_count ?? 0,
              sub: "Graduated curriculum",
            },
            {
              label: "Avg. Completion",
              value: `${kpis?.average_progress ?? 0}%`,
              sub: `${kpis?.submissions_count ?? 0} GitHub submissions`,
            },
          ].map((item, idx) => (
            <GlassCard
              key={idx}
              className="cht-p-5 cht-rounded-[24px] cht-space-y-1"
            >
              <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                {item.label}
              </p>
              <p className="cht-text-2xl cht-font-black cht-text-[#1e293b]">
                {isLoading ? "—" : item.value}
              </p>
              <p className="cht-text-[11px] cht-text-gray-400 cht-font-medium">
                {item.sub}
              </p>
            </GlassCard>
          ))}
        </div>

        <div className="cht-space-y-6">
          <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
            <div className="cht-relative cht-flex-1 cht-w-full">
              <Search
                className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search students by name, email, GitHub username, or course..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold cht-focus:ring-2 cht-focus:ring-[#3e57da]/10 cht-transition-all"
              />
            </div>
            <div className="cht-relative">
              <Button
                variant="ghost"
                leftIcon={<Filter size={18} />}
                className={`cht-rounded-xl transition-all ${
                  showFilters ||
                  statusFilters.completed ||
                  statusFilters.inProgress
                    ? "cht-bg-[#3e57da]/10 cht-text-[#3e57da]"
                    : "cht-bg-gray-50"
                }`}
                onClick={() => setShowFilters(!showFilters)}
              >
                Advanced Filter
                {(statusFilters.completed || statusFilters.inProgress) && (
                  <span className="cht-ml-2 cht-w-2 cht-h-2 cht-bg-[#3e57da] cht-rounded-full"></span>
                )}
              </Button>
              <AnimatePresence>
                {showFilters && (
                  <FilterPopover
                    onClose={() => setShowFilters(false)}
                    sortConfig={sortConfig}
                    setSortConfig={setSortConfig}
                    statusFilters={statusFilters}
                    setStatusFilters={setStatusFilters}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Course Filter Pills */}
          <div className="cht-flex cht-items-center cht-gap-2 cht-overflow-x-auto cht-pb-2 cht-scrollbar-hide">
            {courseFilterOptions.map((course) => (
              <button
                key={course}
                onClick={() => setSelectedCourse(course)}
                className={`cht-px-6 cht-py-2.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all cht-whitespace-nowrap ${
                  selectedCourse === course
                    ? "cht-bg-[#3e57da] cht-text-white cht-shadow-md"
                    : "cht-bg-white cht-text-gray-400 hover:cht-bg-gray-50 cht-border cht-border-gray-100"
                }`}
              >
                {course}
              </button>
            ))}
          </div>
        </div>

        <GlassCard className="cht-rounded-[32px] cht-overflow-visible">
          <div className="cht-overflow-x-auto">
            <table className="cht-w-full cht-text-left">
              <thead>
                <tr className="cht-bg-gray-50/50">
                  <th className="cht-px-8 cht-py-5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Student
                  </th>
                  <th className="cht-px-8 cht-py-5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Enrolled Course
                  </th>
                  <th className="cht-px-8 cht-py-5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Progress
                  </th>
                  <th className="cht-px-8 cht-py-5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Last Active
                  </th>
                  <th className="cht-px-8 cht-py-5 cht-text-right"></th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {isLoading &&
                  Array.from({ length: 4 }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="cht-h-[56px]">
                      <td className="cht-px-8 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-4">
                          <div className="cht-w-10 cht-h-10 cht-rounded-[14px] cht-bg-gray-100 cht-animate-pulse" />
                          <div className="cht-space-y-2">
                            <div className="cht-h-3.5 cht-w-32 cht-bg-gray-100 cht-rounded cht-animate-pulse" />
                            <div className="cht-h-2.5 cht-w-40 cht-bg-gray-50 cht-rounded cht-animate-pulse" />
                          </div>
                        </div>
                      </td>
                      <td className="cht-px-8 cht-py-4">
                        <div className="cht-h-3.5 cht-w-44 cht-bg-gray-100 cht-rounded cht-animate-pulse" />
                      </td>
                      <td className="cht-px-8 cht-py-4">
                        <div className="cht-h-2 cht-w-24 cht-bg-gray-100 cht-rounded-full cht-animate-pulse" />
                      </td>
                      <td className="cht-px-8 cht-py-4">
                        <div className="cht-h-3 cht-w-20 cht-bg-gray-100 cht-rounded cht-animate-pulse" />
                      </td>
                      <td className="cht-px-8 cht-py-4" />
                    </tr>
                  ))}

                {!isLoading &&
                  filteredStudents.map((student) => {
                    const isMenuOpen = openActionRowId === student.id;
                    return (
                      <motion.tr
                        layout
                        key={student.id}
                        onClick={() => {
                          setOpenActionRowId(null);
                          setSelectedStudent(student);
                        }}
                        className="cht-group hover:cht-bg-gray-50/80 cht-transition-colors cht-cursor-pointer cht-h-[56px]"
                      >
                        <td className="cht-px-8 cht-py-4">
                          <div className="cht-flex cht-items-center cht-gap-4">
                            <div className="cht-w-10 cht-h-10 cht-rounded-[14px] cht-overflow-hidden cht-shadow-sm group-hover:cht-shadow-md transition-shadow cht-bg-gray-50 cht-flex-shrink-0">
                              <img
                                src={student.avatar || DEFAULT_AVATAR}
                                alt={student.name}
                                className="cht-w-full cht-h-full cht-object-cover"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).src =
                                    DEFAULT_AVATAR;
                                }}
                              />
                            </div>
                            <div>
                              <p className="cht-text-sm cht-font-bold cht-text-gray-900">
                                {student.name}
                              </p>
                              <p className="cht-text-[10px] cht-text-gray-400 cht-font-medium">
                                {student.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="cht-px-8 cht-py-4">
                          <div className="cht-space-y-1">
                            <p className="cht-text-xs cht-font-bold cht-text-gray-700">
                              {student.course}
                            </p>
                            <div className="cht-flex cht-items-center cht-gap-2">
                              <span
                                className={`cht-inline-block cht-px-2 cht-py-0.5 cht-rounded-md cht-text-[9px] cht-font-black cht-uppercase ${
                                  student.delivery_mode === "onsite"
                                    ? "cht-bg-amber-50 cht-text-amber-700"
                                    : "cht-bg-blue-50 cht-text-[#3e57da]"
                                }`}
                              >
                                {student.delivery_mode}
                              </span>
                              <span className="cht-text-[10px] cht-text-gray-400 cht-font-semibold">
                                {student.completed_lessons_count ?? 0}/
                                {student.total_lessons_count ?? 0} lessons &
                                tasks
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="cht-px-8 cht-py-4">
                          <ProgressBar progress={student.progress} />
                        </td>
                        <td className="cht-px-8 cht-py-4">
                          <p className="cht-text-xs cht-font-bold cht-text-gray-400">
                            {student.lastActive}
                          </p>
                          <p className="cht-text-[10px] cht-text-gray-300 cht-font-medium">
                            Joined {student.joined}
                          </p>
                        </td>
                        <td
                          className={`cht-px-8 cht-py-4 cht-text-right cht-relative ${
                            isMenuOpen ? "cht-z-40" : ""
                          }`}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionRowId((prev) =>
                                prev === student.id ? null : student.id
                              );
                            }}
                            className="cht-p-2 cht-rounded-xl cht-text-gray-400 hover:cht-text-[#3e57da] hover:cht-bg-blue-50/60 transition-colors"
                            title="Student actions"
                          >
                            <MoreVertical size={18} />
                          </button>

                          {isMenuOpen && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="cht-absolute cht-right-8 cht-top-12 cht-w-52 cht-bg-white cht-rounded-2xl cht-border cht-border-gray-100 cht-shadow-xl cht-py-2 cht-z-50 cht-text-left"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenActionRowId(null);
                                  setSelectedStudent(student);
                                }}
                                className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] cht-flex cht-items-center cht-gap-2.5"
                              >
                                <Users size={14} className="cht-text-[#3e57da]" />
                                View Student Details
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenDirectMessage(student)}
                                className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] cht-flex cht-items-center cht-gap-2.5"
                              >
                                <Mail size={14} className="cht-text-[#3e57da]" />
                                Message Student
                              </button>
                              {student.submission?.repository_url && (
                                <a
                                  href={student.submission.repository_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setOpenActionRowId(null)}
                                  className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] cht-flex cht-items-center cht-gap-2.5 cht-no-underline"
                                >
                                  <ExternalLink
                                    size={14}
                                    className="cht-text-[#3e57da]"
                                  />
                                  Inspect GitHub Repo
                                </a>
                              )}
                            </div>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}

                {!isLoading && filteredStudents.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="cht-px-8 cht-py-20 cht-text-center"
                    >
                      <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-200">
                        <Users size={32} />
                      </div>
                      <h3 className="cht-text-lg cht-font-black cht-text-gray-900">
                        No students found
                      </h3>
                      <p className="cht-text-sm cht-text-gray-400 cht-font-medium">
                        {students.length === 0
                          ? "No students are currently enrolled in your assigned courses or tracks yet."
                          : "Try adjusting your filters or search query."}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </div>

      {/* Modals & Overlays - Outside animated container */}
      <AnimatePresence>
        {selectedStudent && (
          <motion.div
            key="student-detail-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedStudent(null)}
            className="cht-fixed cht-inset-0 cht-bg-slate-900/60 cht-backdrop-blur-sm cht-z-[10000]"
          />
        )}
        {selectedStudent && (
          <StudentDetailView
            key={`student-detail-${selectedStudent.id}`}
            student={selectedStudent}
            onClose={() => setSelectedStudent(null)}
            onMessage={(stu) => handleOpenDirectMessage(stu)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMessageModal && (
          <MessageModal
            key="student-message-modal"
            onClose={() => setShowMessageModal(false)}
            recipientStudent={messageRecipient}
            courseOptions={courseFilterOptions}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default InstructorStudents;
